import { displayTimeZone } from "./time-zone";

/** Link to a site's address or one of its domains, with the scheme staff chose (http while site certificates are unavailable). */
export function siteHref(host: string, scheme: "https" | "http" | undefined): string {
  return `${scheme === "http" ? "http" : "https"}://${host}`;
}

/**
 * Dates and times are written in the portal's display zone (`time-zone.ts`: the person's own choice, else
 * the business's time zone) and in the reader's locale. A time always says which zone it is in
 * ("9:14 am BST"). A bare calendar date ("2026-11-01") has no zone and is never shifted.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

/** One cached formatter per zone, locale and option set: building them is the slow part. */
function zoned(options: Intl.DateTimeFormatOptions, locale?: string): Intl.DateTimeFormat {
  const timeZone = displayTimeZone();
  const key = `${locale ?? ""}|${timeZone}|${JSON.stringify(options)}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    formatters.set(key, formatter);
  }
  return formatter;
}

/** Writes a moment in the display zone. `locale` is for the few screens that fix one (the notification list). */
export function formatInZone(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  return zoned(options, locale).format(new Date(value));
}

/** The calendar day of a moment in the display zone. */
function dayOf(value: string | number | Date): { year: number; month: number; day: number } {
  const parts = zoned({ year: "numeric", month: "numeric", day: "numeric" }, "en-US").formatToParts(
    new Date(value),
  );
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: part("year"), month: part("month"), day: part("day") };
}

/**
 * The calendar day of a moment in the display zone, as a day number. Two of them differ by the number of
 * calendar days between the moments, whatever daylight saving did in between (a day can be 23 or 25 hours).
 */
export function civilDay(value: string | number | Date): number {
  const { year, month, day } = dayOf(value);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

const TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit", timeZoneName: "short" };
const DATE_TIME: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", ...TIME };
const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

/** "27 Oct 2026, 4:30 pm GMT" */
export function formatDateTime(iso: string | null | undefined): string {
  return iso ? formatInZone(iso, DATE_TIME) : "—";
}

/** "27 Oct 2026": the day it is in the display zone. */
export function formatDate(iso: string | null | undefined): string {
  return iso ? formatInZone(iso, { dateStyle: "medium" }) : "—";
}

/** "Deletes in 12 days": the days left by the dates the customer sees, not 24-hour blocks. */
export function deletionCountdown(iso: string, now: number = Date.now()): string {
  if (new Date(iso).getTime() <= now) return "Being deleted";
  const days = civilDay(iso) - civilDay(now);
  if (days <= 0) return "Deletes today";
  return `Deletes in ${days} ${days === 1 ? "day" : "days"}`;
}

/** "4:30 pm GMT" */
export function formatTime(iso: string | null | undefined): string {
  return iso ? formatInZone(iso, TIME) : "—";
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31_536_000],
  ["month", 2_592_000],
  ["week", 604_800],
  ["day", 86_400],
  ["hour", 3_600],
  ["minute", 60],
];

export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

/**
 * A price in minor units: "£39.00", or "Free" for nothing. Prices are pence in GBP unless the plan says
 * otherwise, always with two decimals so "£39.50" never reads as "£40".
 */
export function formatPrice(cents: number, currency = "GBP"): string {
  if (cents === 0) return "Free";
  return formatMoney(cents, currency);
}

/** An amount in minor units: "£39.00" (or "£39" with `whole`). */
export function formatMoney(
  cents: number,
  currency = "GBP",
  { whole = false }: { whole?: boolean } = {},
): string {
  const digits = whole && cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "")).toUpperCase() || "?";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "21 Sep": the dashboard's compact date. */
export function formatDayMonth(iso: string | null | undefined): string {
  if (!iso) return "—";
  const { day, month } = dayOf(iso);
  return `${day} ${MONTHS[month - 1]}`;
}

/**
 * "1 Nov 2026": a calendar date as Growth Intelligence writes it. A bare date ("2026-11-01") is that day
 * wherever the reader is, not midnight UTC.
 */
export function formatDayMonthYear(iso: string | null | undefined): string {
  if (!iso) return "—";
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (bare) return `${Number(bare[3])} ${MONTHS[Number(bare[2]) - 1]} ${bare[1]}`;
  const { day, month, year } = dayOf(iso);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/** "21 Sep, 4:02 pm BST": activity timestamps. */
export function formatActivityTime(iso: string | null | undefined): string {
  return iso ? `${formatDayMonth(iso)}, ${formatTime(iso)}` : "—";
}

/** "Today, 9:14 am BST", "Yesterday, 4:02 pm BST", then "2 days ago": when something recent happened. */
export function formatRecent(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const days = civilDay(now) - civilDay(iso);
  if (days === 0) return `Today, ${formatTime(iso)}`;
  if (days === 1) return `Yesterday, ${formatTime(iso)}`;
  return timeAgo(iso, now.getTime());
}

/** "9:14 am BST": when a card's data was last fetched. */
export function formatClock(ms: number): string {
  return formatInZone(ms, TIME);
}
